// Fournisseur de langue + routeur, réunis (App.js utilise <I18nRouter> à la place de <BrowserRouter>).
//
// Comment le préfixe d'adresse marche SANS toucher aux composants : le routeur est créé avec
// basename="/en" (ou /de, /es…). Résultat : tous les <Link to="/panier"> mènent à /en/panier,
// navigate('/') va à /en/, et useLocation().pathname ne montre jamais le préfixe — les routes et les
// tests d'adresse existants (« startsWith('/carte-a-gratter/') »…) fonctionnent tels quels.
// Changer de langue = changer l'adresse puis recréer le routeur avec le nouveau préfixe (key=…).
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { I18nContext } from './context';
import { DEFAULT_LANG, LANGUAGES, SHELL_NAMESPACES, getLanguage, isEnabledLang } from './config';
import { isRecipientPath, langFromPath, localizedPath, stripLang } from './paths';
import { resolveInitialLang, storeLang } from './detect';
import { getVersion, loadNamespaces, namespacesLoaded, subscribe } from './store';

const readLocation = () => ({ pathname: window.location.pathname, search: window.location.search, hash: window.location.hash });

const initialState = () => {
  const r = resolveInitialLang(readLocation());
  if (r.redirectTo) {
    try { window.history.replaceState(window.history.state, '', r.redirectTo); } catch (e) { /* rien */ }
  }
  return { lang: r.lang, prefix: r.prefix };
};

export default function I18nRouter({ children }) {
  const [state, setState] = useState(initialState);
  const [override, setOverrideState] = useState(null);
  const overrideToken = useRef(0);
  useSyncExternalStore(subscribe, getVersion); // relance le rendu quand un fichier de langue arrive

  const lang = override && isEnabledLang(override) ? override : state.lang;
  const language = getLanguage(lang);

  // Chargement des espaces de l'enveloppe du site (en-tête, bandeau, pied de page) en parallèle.
  useEffect(() => { if (lang !== DEFAULT_LANG) loadNamespaces(lang, SHELL_NAMESPACES); }, [lang]);

  // <html lang="…" dir="…"> : lu par les lecteurs d'écran, les navigateurs (traduction automatique,
  // césure) et les moteurs de recherche.
  useEffect(() => {
    document.documentElement.lang = language.code;
    // Le français (et toute langue de gauche à droite) garde <html> tel qu'avant : pas d'attribut dir.
    if (language.dir && language.dir !== 'ltr') document.documentElement.dir = language.dir;
    else document.documentElement.removeAttribute('dir');
  }, [language.code, language.dir]);

  // Langue imposée par une page de destinataire (useRecipientLang) : on charge d'abord, on applique ensuite.
  const setOverride = useCallback((code) => {
    overrideToken.current += 1;
    const token = overrideToken.current;
    if (!code || !isEnabledLang(code)) { setOverrideState(null); return; }
    loadNamespaces(code, SHELL_NAMESPACES).then(() => { if (token === overrideToken.current) setOverrideState(code); });
  }, []);

  // Choix explicite du visiteur (sélecteur) : on prépare la langue, on change l'adresse, on mémorise.
  const setLanguage = useCallback(async (code) => {
    if (!isEnabledLang(code)) return;
    if (code !== DEFAULT_LANG) await loadNamespaces(code, SHELL_NAMESPACES);
    const { pathname, search, hash } = readLocation();
    const target = localizedPath(stripLang(pathname), code) + search + hash;
    try { window.history.pushState(null, '', target); } catch (e) { /* rien */ }
    storeLang(code);
    setState({ lang: code, prefix: code === DEFAULT_LANG ? '' : code });
  }, []);

  // Bouton « Précédent » / « Suivant » entre deux langues : l'adresse a changé de préfixe, le routeur
  // doit être recréé avec le bon (sinon il ne reconnaît plus l'adresse).
  useEffect(() => {
    const onPop = async () => {
      const { pathname } = readLocation();
      const prefix = langFromPath(pathname) || '';
      // Adresse sans préfixe atteinte par « Précédent » : c'est du français (sauf page de destinataire).
      const next = prefix || (isRecipientPath(pathname) ? resolveInitialLang(readLocation()).lang : DEFAULT_LANG);
      if (prefix === state.prefix && next === state.lang) return;
      if (next !== DEFAULT_LANG) await loadNamespaces(next, SHELL_NAMESPACES);
      setState({ lang: next, prefix });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [state]);

  const value = useMemo(() => ({
    lang, urlLang: state.prefix || DEFAULT_LANG, locale: language.locale, languages: LANGUAGES, setLanguage, setOverride,
  }), [lang, state.prefix, language.locale, setLanguage, setOverride]);

  // Première image d'une langue autre que le français : on attend l'enveloppe (quelques Ko). Une fois
  // l'application affichée on ne la retire plus (changer de langue précharge avant de basculer).
  const started = useRef(false);
  if (state.lang === DEFAULT_LANG || namespacesLoaded(state.lang, SHELL_NAMESPACES)) started.current = true;
  const ready = started.current;

  return (
    <I18nContext.Provider value={value}>
      {ready && (
        <BrowserRouter key={state.prefix || DEFAULT_LANG} basename={state.prefix ? `/${state.prefix}` : undefined}>
          <Suspense fallback={null}>{children}</Suspense>
        </BrowserRouter>
      )}
    </I18nContext.Provider>
  );
}
